<template>
  <div class="cantidades-netas">
    <h3 class="section-title">
      <IconChartBar class="section-icon" />
      Cantidades Netas
    </h3>
    <el-row :gutter="16">
      <el-col
        :xs="24"
        :md="12"
      >
        <div class="sub-section">
          <h4 class="sub-title">Sin merma (iniciales)</h4>
          <el-row :gutter="8">
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">Unidades</span>
                <span class="data-value">{{ dv(iniciales.unidades, '', 0) }}</span>
              </div>
            </el-col>
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">Metros lineales</span>
                <span class="data-value">{{ dv(iniciales.metrosLineales, 'm') }}</span>
              </div>
            </el-col>
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">m²</span>
                <span class="data-value">{{ dv(iniciales.metrosCuadrados, 'm2') }}</span>
              </div>
            </el-col>
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">Kilogramos</span>
                <span class="data-value">{{ dv(iniciales.kilogramos, 'kg') }}</span>
              </div>
            </el-col>
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">Ciclos de impresión</span>
                <span class="data-value">{{ dv(iniciales.pliegos, 'pliegos') }}</span>
              </div>
            </el-col>
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">Gramaje total</span>
                <span class="data-value">{{ dv(producto.gramajeTotal, 'g/m2') }}</span>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-col>

      <el-col
        :xs="24"
        :md="12"
      >
        <div class="sub-section">
          <h4 class="sub-title">Con merma (finales)</h4>
          <el-row :gutter="8">
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">Unidades</span>
                <span class="data-value">{{ dv(conMerma.unidades, '', 0) }}</span>
              </div>
            </el-col>
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">Metros lineales</span>
                <span class="data-value">{{ dv(conMerma.metrosLineales, 'm') }}</span>
              </div>
            </el-col>
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">m²</span>
                <span class="data-value">{{ dv(conMerma.metrosCuadrados, 'm2') }}</span>
              </div>
            </el-col>
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">Kilogramos</span>
                <span class="data-value">{{ dv(conMerma.kilogramos, 'kg') }}</span>
              </div>
            </el-col>
            <el-col
              :xs="12"
              :sm="8"
            >
              <div class="data-item">
                <span class="data-label">Ciclos de impresión</span>
                <span class="data-value">{{ dv(conMerma.pliegos, 'pliegos') }}</span>
              </div>
            </el-col>
          </el-row>
        </div>
      </el-col>
    </el-row>

    <div
      class="merma-global"
      :class="`merma-${colorMerma}`"
    >
      <span class="merma-label">Merma global:</span>
      <span class="merma-valor">{{ dp(merma.porcentaje) }}</span>
      <span
        v-if="!isStale"
        class="merma-detalle"
      >
        ({{ formatearNumero(merma.diferencia, 0) }}
        {{ merma.unidadEvaluada?.toLowerCase() || 'unidades' }} adicionales)
      </span>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import IconChartBar from '~icons/mdi/chart-bar';
import {
  displayValueConUnidad,
  displayPorcentaje,
  formatearNumero,
  obtenerColorMerma,
} from '@/shared/utils/format_produccion.js';

const props = defineProps({
  cantidadDatos: {
    type: Object,
    required: true,
  },
  producto: {
    type: Object,
    default: () => ({}),
  },
  isStale: {
    type: Boolean,
    default: false,
  },
});

const iniciales = computed(() => props.cantidadDatos?.totalesIniciales || {});
const conMerma = computed(() => props.cantidadDatos?.totalesConMerma || {});
const merma = computed(() => props.cantidadDatos?.mermaGlobal || {});
const colorMerma = computed(() => obtenerColorMerma(merma.value.porcentaje));

function dv(valor, unidad, decimales = 2) {
  return displayValueConUnidad(valor, unidad, props.isStale, decimales);
}

function dp(valor) {
  return displayPorcentaje(valor, props.isStale);
}
</script>

<style scoped>
.cantidades-netas {
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 16px;
}

.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #303133;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 6px;
}

.section-icon {
  font-size: 18px;
  color: #409eff;
}

.sub-section {
  padding: 8px 0;
}

.sub-title {
  font-size: 12px;
  font-weight: 600;
  color: #606266;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.data-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px 0;
}

.data-label {
  font-size: 11px;
  color: #909399;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.data-value {
  font-size: 14px;
  font-weight: 600;
  color: #303133;
  font-family: 'Fira Code', monospace;
}

.merma-global {
  margin-top: 12px;
  padding: 10px 16px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
}

.merma-label {
  font-weight: 600;
}

.merma-valor {
  font-weight: 700;
  font-family: 'Fira Code', monospace;
}

.merma-detalle {
  color: #606266;
  font-size: 12px;
}

.merma-success {
  background: #f0f9eb;
  border: 1px solid #e1f3d8;
  color: #67c23a;
}

.merma-warning {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  color: #e6a23c;
}

.merma-error {
  background: #fef0f0;
  border: 1px solid #fde2e2;
  color: #f56c6c;
}

.merma-neutral {
  background: #f4f4f5;
  border: 1px solid #e9e9eb;
  color: #909399;
}
</style>
